import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Gear,
    Eye,
    Plus,
    Pencil,
} from '@phosphor-icons/react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';

import { Spinner } from '@/components/ui';
import { FormFieldEditor } from '@/components/features/FormFieldEditor/FormFieldEditor';
import { useFormBuilderStore } from '@/stores';
import { useCreateForm } from '@/api';
import { buildFormShareUrl } from '@/constants/routes';
import { sanitizeString } from '@/lib/sanitize';

import { SectionEditor } from './components/SectionEditor/SectionEditor';
import { BuilderPreview } from './components/BuilderPreview/BuilderPreview';
import { FormSettings } from './components/FormSettings/FormSettings';

import type { CreateFormFieldPayload } from '@/types/form';

import styles from './FormBuilder.module.css';

type PanelTab = 'editor' | 'preview' | 'settings';

interface SortableFieldItemProps {
    id: string;
    children: React.ReactNode;
}

function SortableFieldItem({ id, children }: SortableFieldItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
            }}
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
}

export function FormBuilder() {
    const navigate = useNavigate();
    const createForm = useCreateForm();

    const {
        sections,
        selectedSectionIndex,
        selectedFieldIndex,
        formSettings,
        isDirty,
        addSection,
        removeSection,
        reorderFields,
        addField,
        removeField,
        updateField,
        updateSettings,
        updateSection,
        setSelectedSection,
        setSelectedField,
        reset,
    } = useFormBuilderStore();

    const [activeTab, setActiveTab] = useState<PanelTab>('editor');

    useEffect(() => {
        document.title = 'Crear Formulario — Ratio';
        return () => { reset(); };
    }, [reset]);

    const selectedSection = sections[selectedSectionIndex] ?? sections[0];
    const totalFields = sections.reduce((acc, s) => acc + s.fields.length, 0);

    const validateForm = useCallback((): boolean => {
        if (!formSettings.title.trim()) {
            toast.error('El titulo es obligatorio');
            return false;
        }
        if (totalFields === 0) {
            toast.error('Agrega al menos una pregunta');
            return false;
        }
        for (const section of sections) {
            for (let i = 0; i < section.fields.length; i++) {
                const f = section.fields[i];
                if (!f.label.trim()) {
                    toast.error(`La pregunta ${i + 1} de "${section.title || 'la seccion'}" no tiene texto`);
                    return false;
                }
                if (['single_choice', 'multiple_choice', 'dropdown'].includes(f.field_type)) {
                    const choices = (f.config['choices'] as string[] | undefined) ?? [];
                    const valid = choices.filter((c) => c.trim());
                    if (valid.length < 2) {
                        toast.error(`La pregunta "${f.label}" necesita al menos 2 opciones`);
                        return false;
                    }
                }
            }
        }
        if (totalFields > 50) {
            toast.error('Maximo 50 preguntas por formulario');
            return false;
        }
        return true;
    }, [formSettings.title, sections, totalFields]);

    const handlePublish = () => {
        if (!validateForm()) return;

        const sanitizedSections = sections.map((s, si) => ({
            title: sanitizeString(s.title.trim()),
            description: s.description?.trim() ? sanitizeString(s.description.trim()) : '',
            order: si,
            fields: s.fields.map((f, fi) => ({
                ...f,
                label: sanitizeString(f.label.trim()),
                order: fi,
            })),
        }));

        createForm.mutate(
            {
                title: sanitizeString(formSettings.title.trim()),
                description: formSettings.description.trim() ? sanitizeString(formSettings.description.trim()) : undefined,
                visibility: formSettings.visibility,
                requires_auth: formSettings.requires_auth,
                one_response_per_user: formSettings.one_response_per_user,
                closes_at: formSettings.closes_at || null,
                sections_data: sanitizedSections,
                mode: formSettings.mode,
                shuffle_options: formSettings.shuffle_options,
                allow_edit_response: formSettings.allow_edit_response,
                show_progress_bar: formSettings.show_progress_bar,
                confirmation_message: formSettings.confirmation_message,
                theme_color: formSettings.theme_color,
                theme_image_url: formSettings.theme_image_url,
                collect_email: formSettings.collect_email,
            },
            {
                onSuccess: (form) => {
                    toast.success('Formulario publicado!');
                    navigate(buildFormShareUrl(form.author.slug, form.slug));
                },
                onError: (err: unknown) => {
                    const apiErr = err as { response?: { data?: Record<string, string[]> } };
                    const firstMsg = apiErr?.response?.data
                        ? Object.values(apiErr.response.data).flat()[0]
                        : null;
                    toast.error(firstMsg ?? 'No se pudo publicar el formulario. Intenta de nuevo.', { duration: 8000 });
                },
            }
        );
    };

    const handleFieldChange = useCallback((fieldIndex: number, field: CreateFormFieldPayload) => {
        updateField(selectedSectionIndex, fieldIndex, field);
    }, [selectedSectionIndex, updateField]);

    const handleFieldDelete = useCallback((fieldIndex: number) => {
        removeField(selectedSectionIndex, fieldIndex);
    }, [selectedSectionIndex, removeField]);

    const handleDragEnd = (event: { active: { id: string | number }; over: { id: string | number } | null }) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const fields = selectedSection?.fields ?? [];
        const fromIndex = fields.findIndex((_, i) => `field-${i}` === String(active.id));
        const toIndex = fields.findIndex((_, i) => `field-${i}` === String(over.id));
        if (fromIndex !== -1 && toIndex !== -1) {
            reorderFields(selectedSectionIndex, fromIndex, toIndex);
        }
    };

    const currentFields = selectedSection?.fields ?? [];

    return (
        <div className={styles.page}>
            {/* Top bar */}
            <header className={styles.topBar}>
                <button
                    className={styles.backBtn}
                    onClick={() => navigate(-1)}
                    aria-label="Volver"
                >
                    <ArrowLeft size={18} />
                    Volver
                </button>

                <div className={styles.titleArea}>
                    <input
                        className={styles.titleInput}
                        value={formSettings.title}
                        onChange={(e) => updateSettings({ title: e.target.value })}
                        placeholder="Titulo del formulario"
                        maxLength={200}
                    />
                    {isDirty && <span className={styles.dirtyIndicator}>Sin guardar</span>}
                </div>

                <div className={styles.topActions}>
                    <div className={styles.tabBtns}>
                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === 'editor' ? styles.tabBtnActive : ''}`}
                            onClick={() => setActiveTab('editor')}
                        >
                            <Pencil size={16} />
                            Editar
                        </button>
                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === 'preview' ? styles.tabBtnActive : ''}`}
                            onClick={() => setActiveTab('preview')}
                        >
                            <Eye size={16} />
                            Vista previa
                        </button>
                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === 'settings' ? styles.tabBtnActive : ''}`}
                            onClick={() => setActiveTab('settings')}
                        >
                            <Gear size={16} />
                            Ajustes
                        </button>
                    </div>
                    <button
                        type="button"
                        className={styles.publishBtn}
                        onClick={handlePublish}
                        disabled={createForm.isPending}
                    >
                        <span style={{ visibility: createForm.isPending ? 'hidden' : 'visible' }}>Publicar</span>
                        {createForm.isPending && <Spinner size="sm" variant="inverse" className={styles.btnSpinner} />}
                    </button>
                </div>
            </header>

            <div className={styles.body}>
                {/* Left panel: section list */}
                <aside className={styles.leftPanel}>
                    <div className={styles.sectionListHeader}>
                        <span className={styles.sectionListTitle}>Secciones</span>
                    </div>

                    <div className={styles.sectionList}>
                        {sections.map((section, si) => (
                            <SectionEditor
                                key={si}
                                section={section}
                                sectionIndex={si}
                                isSelected={si === selectedSectionIndex}
                                onSelect={() => setSelectedSection(si)}
                                onUpdate={(partial) => updateSection(si, partial)}
                                onDelete={() => removeSection(si)}
                                onAddField={() => { setSelectedSection(si); addField(si); }}
                                canDelete={sections.length > 1}
                            />
                        ))}
                    </div>

                    <button
                        type="button"
                        className={styles.addSectionBtn}
                        onClick={addSection}
                    >
                        <Plus size={14} />
                        Agregar seccion
                    </button>
                </aside>

                {/* Center / right panels based on tab */}
                <main className={styles.mainArea}>
                    {activeTab === 'editor' && (
                        <div className={styles.editorPanel}>
                            {!selectedSection ? (
                                <div className={styles.emptyEditor}>
                                    <p className={styles.emptyText}>Empieza agregando una seccion</p>
                                    <button type="button" className={styles.emptyBtn} onClick={addSection}>
                                        <Plus size={16} />
                                        Agregar seccion
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Section header edit */}
                                    <div className={styles.sectionHeader}>
                                        <input
                                            className={styles.sectionTitleInput}
                                            value={selectedSection.title}
                                            onChange={(e) => updateSection(selectedSectionIndex, { title: e.target.value })}
                                            placeholder={`Seccion ${selectedSectionIndex + 1}`}
                                            maxLength={200}
                                        />
                                        <input
                                            className={styles.sectionDescInput}
                                            value={selectedSection.description ?? ''}
                                            onChange={(e) => updateSection(selectedSectionIndex, { description: e.target.value })}
                                            placeholder="Descripcion de la seccion (opcional)"
                                            maxLength={500}
                                        />
                                    </div>

                                    {/* Fields */}
                                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                        <SortableContext
                                            items={currentFields.map((_, i) => `field-${i}`)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <div className={styles.fieldsList}>
                                                {currentFields.length === 0 ? (
                                                    <div className={styles.emptyFields}>
                                                        <p className={styles.emptyText}>No hay preguntas en esta seccion</p>
                                                    </div>
                                                ) : (
                                                    currentFields.map((field, fi) => (
                                                        <SortableFieldItem key={`field-${fi}`} id={`field-${fi}`}>
                                                            <div
                                                                className={`${styles.fieldItem} ${fi === selectedFieldIndex ? styles.fieldItemSelected : ''}`}
                                                                onClick={() => setSelectedField(fi)}
                                                                role="button"
                                                                tabIndex={0}
                                                                aria-label={`Pregunta ${fi + 1}${field.label ? `: ${field.label}` : ''}`}
                                                                onKeyDown={(e) => e.key === 'Enter' && setSelectedField(fi)}
                                                            >
                                                                <FormFieldEditor
                                                                    field={field}
                                                                    index={fi}
                                                                    onChange={handleFieldChange}
                                                                    onDelete={handleFieldDelete}
                                                                    canDelete={currentFields.length > 1}
                                                                    isQuizMode={formSettings.mode === 'quiz'}
                                                                />
                                                            </div>
                                                        </SortableFieldItem>
                                                    ))
                                                )}
                                            </div>
                                        </SortableContext>
                                    </DndContext>

                                    {totalFields < 50 && (
                                        <button
                                            type="button"
                                            className={styles.addFieldBtn}
                                            onClick={() => addField(selectedSectionIndex)}
                                        >
                                            <Plus size={16} />
                                            Agregar pregunta
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'preview' && (
                        <BuilderPreview
                            title={formSettings.title}
                            description={formSettings.description}
                            sections={sections}
                            mode={formSettings.mode}
                            themeColor={formSettings.theme_color}
                        />
                    )}

                    {activeTab === 'settings' && (
                        <div className={styles.settingsPanel}>
                            <FormSettings
                                settings={formSettings}
                                onUpdate={updateSettings}
                            />
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
